import { createReducer } from '@reduxjs/toolkit';

import { DeliveryOptionsDto } from '../models/dto/delivery-options-dto';
import { ShoppingCartDto, ShoppingCartItemDto, ShoppingCartSupplierDto } from '../models/dto/shopping-cart-dto';
import { defaultsChanged, shoppingCartDeliveryOptionsReceived } from './shopping-cart-delivery-options-actions';
import {
    shoppingCartEmptied,
    shoppingCartMinimalReceived,
    shoppingCartReceived,
    shoppingCartSingleInquirySubmitted,
    shoppingCartSubmitted,
} from './shopping-cart-actions';
import {
    shoppingCartItemAdded,
    shoppingCartItemQuantityChanged,
    shoppingCartItemRemoved,
} from './shopping-cart-article-actions';

export interface ShoppingCartState {
    shoppingCart?: ShoppingCartDto;
    deliveryOptions?: DeliveryOptionsDto;
    subtotal: number;
    itemCount: number;
}

const initialState: ShoppingCartState = {
    subtotal: 0,
    itemCount: 0,
};

export const shoppingCartReducer = createReducer(initialState, builder => {
    builder.addCase(shoppingCartReceived, (state, action) => {
        if (!action.payload) {
            return state;
        }

        return {
            ...state,
            shoppingCart: action.payload,
            subtotal: action.payload.subtotal,
            itemCount: action.payload.itemCount,
        };
    });
    builder.addCase(shoppingCartDeliveryOptionsReceived, (state, action) => ({
        ...state,
        deliveryOptions: action.payload,
    }));
    builder.addCase(shoppingCartEmptied, state => ({
        ...state,
        shoppingCart: undefined,
        subtotal: 0,
        itemCount: 0,
    }));
    builder.addCase(shoppingCartSubmitted, state => ({
        ...state,
        deliveryOptions: undefined,
        shoppingCart: undefined,
        subtotal: 0,
        itemCount: 0,
    }));
    builder.addCase(shoppingCartSingleInquirySubmitted, state => ({
        ...state,
        deliveryOptions: undefined,
        shoppingCart: undefined,
        subtotal: 0,
        itemCount: 0,
    }));
    builder.addCase(shoppingCartItemQuantityChanged, (state, action) => {
        if (!state.shoppingCart) {
            return state;
        }

        let nextSuppliers = modifySupplierByItemId(state.shoppingCart.suppliers, action.payload.itemId, () => ({
            deliveryCosts: action.payload.priceChange.supplierDeliveryCosts,
            surcharges: action.payload.priceChange.supplierSurcharges,
        }));

        nextSuppliers = modifyItem(nextSuppliers, action.payload.itemId, item => ({
            netPrice: action.payload.priceChange.netPrice,
            quantity: action.payload.quantity,
            product: {
                ...item.product,
                prices: {
                    ...item.product.prices,
                    ...action.payload.priceChange.prices[0],
                },
            },
        }));

        return {
            ...state,
            shoppingCart: {
                ...state.shoppingCart,
                suppliers: nextSuppliers,
            },
            subtotal: action.payload.priceChange.subtotal,
        };
    });
    builder.addCase(shoppingCartItemRemoved, (state, action) => {
        if (!state.shoppingCart) {
            return state;
        }

        let nextSuppliers = modifySupplierByItemId(state.shoppingCart.suppliers, action.payload.itemId, supplier => ({
            surcharges: action.payload.priceChange.supplierSurcharges,
            deliveryCosts: action.payload.priceChange.supplierDeliveryCosts,
            items: supplier.items?.filter(item => item.id !== action.payload.itemId),
        }));

        nextSuppliers = nextSuppliers.filter(supplier => supplier.items && supplier.items.length > 0);

        const hasUnavailableItems = nextSuppliers.some(supplier =>
            supplier.items?.some(item => !item.isAvailableForPurchase)
        );

        return {
            ...state,
            shoppingCart: {
                ...state.shoppingCart,
                suppliers: nextSuppliers,
                hasUnavailableItems,
            },
            subtotal: action.payload.priceChange.subtotal,
        };
    });

    builder.addCase(shoppingCartMinimalReceived, (state, action) => ({
        ...state,
        ...action.payload,
    }));

    builder.addCase(shoppingCartItemAdded, (state, action) => {
        return {
            ...state,
            ...action.payload,
        };
    });
    builder.addCase(defaultsChanged, (state, action) => {
        if (!state.deliveryOptions) {
            return state;
        }

        return {
            ...state,
            deliveryOptions: {
                ...state.deliveryOptions,
                defaults: {
                    ...action.payload,
                },
            },
        };
    });
});

const modifySupplierByItemId = (
    suppliers: ShoppingCartSupplierDto[],
    itemId: number,
    callback: (supplier: ShoppingCartSupplierDto) => Partial<ShoppingCartSupplierDto>
): ShoppingCartSupplierDto[] => {
    const nextSuppliers = [...suppliers];
    const supplierIndex = nextSuppliers.findIndex(supplier => supplier.items?.some(item => item.id === itemId));
    if (supplierIndex < 0) {
        return suppliers;
    }

    nextSuppliers[supplierIndex] = {
        ...nextSuppliers[supplierIndex],
        ...callback(nextSuppliers[supplierIndex]),
    };
    return nextSuppliers;
};

const modifyItem = (
    suppliers: ShoppingCartSupplierDto[],
    itemId: number,
    callback: (article: ShoppingCartItemDto) => Partial<ShoppingCartItemDto>
): ShoppingCartSupplierDto[] => {
    return modifySupplierByItemId(suppliers, itemId, supplier => {
        const articleIndex = supplier.items?.findIndex(item => item.id === itemId);
        if (!supplier.items || articleIndex === undefined || articleIndex < 0) {
            return supplier;
        }

        const nextSupplierItems = [...supplier.items];
        nextSupplierItems[articleIndex] = {
            ...nextSupplierItems[articleIndex],
            ...callback(nextSupplierItems[articleIndex]),
        };
        return { ...supplier, items: nextSupplierItems };
    });
};
